/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { Icon } from '@fluentui/react/lib/Icon';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AuthoringRegion } from 'src/core/types/regionTypes';
import { LuisId } from 'src/luis/types/Resource';
import { AppToExportStore } from 'src/verticals/azureMigrationModal/stores/AppToExportStore';

export type AppsToExportTableProps = TableProps<AppToExportStore> & {
    onExportApp: (id: LuisId, appName: string, region: AuthoringRegion) => void;
};

const messages = defineMessages({
    nameColumnHeader: {
        id: 'azureMigrationModal.appsToExportTable.nameColumnHeader',
        defaultMessage: 'App Name'
    },
    regionColumnHeader: {
        id: 'azureMigrationModal.appsToExportTable.regionColumnHeader',
        defaultMessage: 'Region'
    },
    ownerEmailColumnHeader: {
        id: 'azureMigrationModal.appsToExportTable.collaboratorsColumnHeader',
        defaultMessage: 'Owner'
    },
    emptyTablePlaceholder: {
        id: 'azureMigrationModal.appsToExportTable.emptyTablePlaceholder',
        defaultMessage: 'You have no apps that you are a collaborator on.'
    }
});

/**
 * Displays a table of Apps and their Collaborators.
 *
 * @param props The input properties for the table.
 */
export const AppsToExportTable = (props: AppsToExportTableProps) => {
    const { onExportApp, items, selection, ...rest } = props;
    const { localize } = useLocalization();

    const columns: IColumn[] = [
        {
            key: 'ExportApp',
            name: '',
            fieldName: '',
            minWidth: 20,
            maxWidth: 20,
            isResizable: true,
            isCollapsible: true,
            onRender: (item: AppToExportStore) => (
                <Link onClick={() => onExportApp(item.appId, item.appName, item.region)}>
                    <Icon iconName="Download" />
                </Link>
            )
        },
        {
            key: 'appName',
            name: localize(messages.nameColumnHeader),
            fieldName: 'appName',
            minWidth: 100,
            maxWidth: 100,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            onRender: (item: AppToExportStore) => (
                <Stack verticalFill verticalAlign="center">
                    {item.hasPredictionResource ? `* ${item.appName}` : item.appName}
                </Stack>
            )
        },
        {
            key: 'region',
            name: localize(messages.regionColumnHeader),
            fieldName: 'region',
            minWidth: 100,
            maxWidth: 100,
            isResizable: true,
            isCollapsible: true,
            onRender: (item: AppToExportStore) => item.region
        },
        {
            key: 'ownerEmail',
            name: localize(messages.ownerEmailColumnHeader),
            fieldName: 'ownerEmail',
            minWidth: 100,
            isResizable: true,
            isCollapsible: true,
            onRender: (item: AppToExportStore) => item.ownerEmail
        }
    ];

    return (
        <Table<AppToExportStore>
            items={items}
            columns={columns}
            selection={selection}
            selectionMode={SelectionMode.none}
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            {...rest}
        />
    );
};
